import React, { useState, useEffect } from 'react';
import { 
    IconPlus, 
    IconTrash, 
    IconCheck, 
    IconBriefcase, 
    IconGraduationCap, 
    IconMessageCircle,
    IconMapPin,
    IconPhone
} from '../ui/Icons';

const ResumeModal = ({ isOpen, onClose, initialData, onSave }) => {
    const [resumeData, setResumeData] = useState({
        summary: '',
        education: [],
        experience: [],
        skills: [],
        city: '',
        phone: ''
    });

    useEffect(() => {
        if (initialData) {
            setResumeData({
                summary: initialData.summary || '',
                education: Array.isArray(initialData.education) ? initialData.education : [],
                experience: Array.isArray(initialData.experience) ? initialData.experience : [],
                skills: Array.isArray(initialData.skills) ? initialData.skills : [],
                city: initialData.city || '',
                phone: initialData.phone || ''
            });
        }
    }, [initialData, isOpen]);

    const handleAddItem = (field, defaultObj) => {
        setResumeData(prev => ({
            ...prev,
            [field]: [...prev[field], defaultObj]
        }));
    };

    const handleUpdateItem = (field, index, key, value) => {
        const newList = [...resumeData[field]];
        newList[index] = { ...newList[index], [key]: value };
        setResumeData(prev => ({ ...prev, [field]: newList }));
    };

    const handleRemoveItem = (field, index) => {
        setResumeData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const handleAddSkill = (skill) => {
        if (!skill.trim()) return;
        if (resumeData.skills.includes(skill.trim())) return;
        setResumeData(prev => ({
            ...prev,
            skills: [...prev.skills, skill.trim()]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
            <div className="bg-white dark:bg-gray-900 w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="p-8 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-plantlix-600 text-white">
                    <div>
                        <h2 className="text-3xl font-black">Digitaler Lebenslauf</h2>
                        <p className="text-plantlix-100 font-medium">Stelle dich Betrieben von deiner besten Seite vor.</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all"
                    >
                        <span className="text-2xl">×</span>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-12">
                    {/* Summary */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-plantlix-100 dark:bg-plantlix-500/20 rounded-xl flex items-center justify-center">
                                <IconMessageCircle className="w-5 h-5 text-plantlix-600 dark:text-plantlix-400" />
                            </div>
                            <h3 className="text-xl font-black">Über mich</h3>
                        </div>
                        <textarea 
                            className="w-full p-6 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl outline-none focus:ring-2 focus:ring-plantlix-500 font-medium min-h-[120px]"
                            placeholder="Beschreibe kurz deine Motivation und wer du bist..."
                            value={resumeData.summary}
                            onChange={(e) => setResumeData({...resumeData, summary: e.target.value})}
                        />
                    </section>

                    {/* Contact */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center">
                                <IconMapPin className="w-5 h-5 text-plantlix-500 dark:text-blue-400" />
                            </div>
                            <h3 className="text-xl font-black">Kontakt & Standort</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative">
                                <IconMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input 
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl font-bold"
                                    placeholder="Wohnort (z.B. Berlin)"
                                    value={resumeData.city}
                                    onChange={(e) => setResumeData({...resumeData, city: e.target.value})}
                                />
                            </div>
                            <div className="relative">
                                <IconPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input 
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl font-bold"
                                    placeholder="Telefonnummer"
                                    value={resumeData.phone}
                                    onChange={(e) => setResumeData({...resumeData, phone: e.target.value})}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Experience */}
                    <section>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-500/20 rounded-xl flex items-center justify-center">
                                    <IconBriefcase className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                </div>
                                <h3 className="text-xl font-black">Berufserfahrung</h3>
                            </div>
                            <button 
                                onClick={() => handleAddItem('experience', { company: '', role: '', period: '', description: '' })}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-black shadow-lg shadow-amber-600/20"
                            >
                                <IconPlus className="w-4 h-4" /> Hinzufügen
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {resumeData.experience.map((item, idx) => (
                                <div key={idx} className="p-6 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl relative group">
                                    <button 
                                        onClick={() => handleRemoveItem('experience', idx)}
                                        className="absolute top-4 right-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <IconTrash className="w-5 h-5" />
                                    </button>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <input 
                                            placeholder="Unternehmen" 
                                            className="bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-amber-500"
                                            value={item.company}
                                            onChange={(e) => handleUpdateItem('experience', idx, 'company', e.target.value)}
                                        />
                                        <input 
                                            placeholder="Position / Rolle" 
                                            className="bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-amber-500"
                                            value={item.role}
                                            onChange={(e) => handleUpdateItem('experience', idx, 'role', e.target.value)}
                                        />
                                    </div>
                                    <input 
                                        placeholder="Zeitraum (z.B. 2021 - 2023)" 
                                        className="w-full bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-amber-500 mb-4"
                                        value={item.period}
                                        onChange={(e) => handleUpdateItem('experience', idx, 'period', e.target.value)}
                                    />
                                    <textarea 
                                        placeholder="Kurze Beschreibung deiner Aufgaben..." 
                                        className="w-full bg-white dark:bg-black/20 p-4 rounded-xl border border-gray-100 dark:border-white/5 font-medium outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                                        rows="2"
                                        value={item.description}
                                        onChange={(e) => handleUpdateItem('experience', idx, 'description', e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Education */}
                    <section>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-500/20 rounded-xl flex items-center justify-center">
                                    <IconGraduationCap className="w-5 h-5 text-plantlix-600 dark:text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-black">Bildung</h3>
                            </div>
                            <button 
                                onClick={() => handleAddItem('education', { school: '', degree: '', year: '' })}
                                className="flex items-center gap-2 px-4 py-2 bg-plantlix-600 text-white rounded-xl text-sm font-black shadow-lg shadow-plantlix-600/20"
                            >
                                <IconPlus className="w-4 h-4" /> Hinzufügen
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {resumeData.education.map((item, idx) => (
                                <div key={idx} className="p-6 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl relative group">
                                    <button 
                                        onClick={() => handleRemoveItem('education', idx)}
                                        className="absolute top-4 right-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <IconTrash className="w-5 h-5" />
                                    </button>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <input 
                                            placeholder="Institution / Schule" 
                                            className="bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                                            value={item.school}
                                            onChange={(e) => handleUpdateItem('education', idx, 'school', e.target.value)}
                                        />
                                        <input 
                                            placeholder="Abschluss / Fachrichtung" 
                                            className="bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                                            value={item.degree}
                                            onChange={(e) => handleUpdateItem('education', idx, 'degree', e.target.value)}
                                        />
                                    </div>
                                    <input 
                                        placeholder="Jahr (z.B. 2020)" 
                                        className="w-full bg-white dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                                        value={item.year}
                                        onChange={(e) => handleUpdateItem('education', idx, 'year', e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Skills */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-plantlix-100 dark:bg-plantlix-500/20 rounded-xl flex items-center justify-center">
                                <IconCheck className="w-5 h-5 text-plantlix-600 dark:text-plantlix-400" />
                            </div>
                            <h3 className="text-xl font-black">Fähigkeiten & Skills</h3>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {resumeData.skills.map((skill, idx) => (
                                <span key={idx} className="px-4 py-2 bg-plantlix-100 text-plantlix-700 dark:bg-plantlix-500/20 dark:text-plantlix-400 rounded-full font-bold flex items-center gap-2 group">
                                    {skill}
                                    <button 
                                        onClick={() => handleRemoveItem('skills', idx)}
                                        className="text-plantlix-400 hover:text-plantlix-600 transition-colors"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                        <input 
                            placeholder="Skill hinzufügen (Enter drücken)..." 
                            className="w-full p-4 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl outline-none focus:ring-2 focus:ring-plantlix-500 font-bold"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddSkill(e.target.value);
                                    e.target.value = '';
                                }
                            }}
                        />
                    </section>
                </div>

                {/* Footer */}
                <div className="p-8 border-t border-gray-100 dark:border-white/5 flex gap-4">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-4 bg-gray-100 dark:bg-white/5 text-gray-400 rounded-2xl font-black transition-all"
                    >
                        Abbrechen
                    </button>
                    <button 
                        onClick={() => onSave(resumeData)}
                        className="flex-[2] py-4 bg-plantlix-600 text-white rounded-2xl font-black shadow-lg shadow-plantlix-600/30 hover:scale-[1.02] active:scale-95 transition-all"
                    >
                        Lebenslauf speichern
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResumeModal;
